import { BillActivationStatus } from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { useFocusEffect } from 'expo-router';
import { useAtomValue } from 'jotai';
import React, { useCallback } from 'react';
import Animated, { FadeIn, LinearTransition } from 'react-native-reanimated';
import { H6Regular, isWeb, View, type ViewProps, YStack } from 'smoothie';

import useGetBillDiscoveryReview from '../../../bills/hooks/useGetBillDiscoveryReview';
import {
    useGetActiveBills,
    useGetAllBills,
} from '../../../bills/hooks/useGetBills';
import { useGetUserHasPendingJobs } from '../../../bills/hooks/useGetUserHasPendingJobs';
import {
    activeAndReviewBillsAtom,
    activeBillsAtom,
} from '../../../bills/state/BillsAtoms';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { formatDollars } from '../../../common/utils/bankingUtils';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import { isLoadingHolisticSummaryAtom } from '../../../holistic/state/HolisticAtoms';
import { useMoneyMapWorkflow } from '../../../roadmap/hooks/useMoneyMapWorkflow';
import BillsChangesDetected from './BillsChangesDetected';
import BillsNotFound from './BillsNotFound';
import BillsReadyForReview from './BillsReadyForReview';
import BillsScanningInProgress from './BillsScanningInProgress';
import BillsSummary from './BillsSummary';

const BillsOnHome = ({ ...viewProps }: ViewProps) => {
    const user = useAuthedUser();

    const { value: goyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);
    const { isWorkflowCompleted } = useMoneyMapWorkflow();

    const storedActiveBills = useAtomValue(activeBillsAtom);
    const activeAndReviewBills = useAtomValue(activeAndReviewBillsAtom);

    const { activeBills, isLoadingActiveBills, refetchBills } =
        useGetActiveBills();

    useFocusEffect(
        useCallback(() => {
            refetchBills();
        }, [storedActiveBills]),
    );

    const { isLoadingAllBills } = useGetAllBills();

    const activeBillsHaveProposedChanges = activeBills.filter(
        (bill) => bill.proposedChanges,
    ).length;

    const hasNewBills = activeAndReviewBills.filter(
        (bill) => bill.activationStatus === BillActivationStatus.REVIEW,
    ).length;

    const {
        userHasPendingJobs,
        isLoadingUserHasPendingJobs,
        refetchUserHasPendingJobs,
    } = useGetUserHasPendingJobs();
    useFocusEffect(
        useCallback(() => {
            refetchUserHasPendingJobs();
        }, [refetchUserHasPendingJobs]),
    );

    const isLoadingHolisticSummary =
        useAtomValue(isLoadingHolisticSummaryAtom) !== false;

    const {
        data: billsReadyForReview,
        isLoading: isLoadingBillsReadyForReview,
    } = useGetBillDiscoveryReview(
        user?.membership?.id ?? '',
        isLoadingHolisticSummary,
    );

    // Behind GOYO, only surface the bills widget once the member has finished
    // their money map GO workflow; hide it while that workflow is incomplete.
    if (goyoEnabled && !isWorkflowCompleted) {
        return null;
    }

    if (
        isLoadingHolisticSummary ||
        isLoadingActiveBills ||
        isLoadingUserHasPendingJobs ||
        isLoadingBillsReadyForReview
    ) {
        return null;
    }

    const getBillsWidget = () => {
        const billsMonthlyTotal = formatDollars(
            activeAndReviewBills.reduce(
                (acc, bill) => acc + (bill.monthlyAmount ?? 0),
                0,
            ),
            false,
        );

        const allBillsAreInReview =
            activeAndReviewBills.length > 0 &&
            activeAndReviewBills.every(
                (bill) => bill.activationStatus === BillActivationStatus.REVIEW,
            );

        // if the member has bills that need review, show the review dialog
        if (
            !allBillsAreInReview &&
            (activeBillsHaveProposedChanges ||
                (hasNewBills && !isLoadingAllBills))
        ) {
            return (
                <BillsChangesDetected
                    billsCount={activeAndReviewBills.length}
                    billsAmount={billsMonthlyTotal}
                />
            );
        }

        // if the member has active bills, show the summary!
        if (!allBillsAreInReview && (activeAndReviewBills ?? []).length > 0) {
            return (
                <BillsSummary
                    billsCount={activeAndReviewBills.length}
                    billsAmount={billsMonthlyTotal}
                />
            );
        }

        // if the member has pending jobs AND has no active bills, that means static bill detection is ongoing, so show the scanning in progress widget
        if (userHasPendingJobs && activeAndReviewBills.length < 1) {
            return <BillsScanningInProgress />;
        }

        // if the member has no active bills AND has bills ready for review, show them the CTA to review bills
        // do not show the CTA if new bills have been identified after the member has gone through bill review (that will be implemented as part of bill monitoring)
        if (allBillsAreInReview) {
            return (
                <BillsReadyForReview
                    billsCount={billsReadyForReview.bills.length}
                />
            );
        }

        // if there are no active bills and no pending jobs, this likely means the member has not linked up any accounts yet, so show the CTA to link accounts
        if (!userHasPendingJobs && activeAndReviewBills.length < 1) {
            return <BillsNotFound />;
        }

        return null;
    };

    const billsWidget = getBillsWidget();

    if (!billsWidget) {
        return null;
    }

    return (
        <View {...viewProps}>
            <Animated.View
                {...(isWeb
                    ? { entering: FadeIn.duration(500) }
                    : { layout: LinearTransition.duration(500) })}
                style={{
                    width: '100%',
                    height: isWeb ? '100%' : undefined,
                    maxHeight: isWeb ? 340 : undefined,
                }}
            >
                <YStack height={isWeb ? '100%' : undefined}>
                    <H6Regular maxWidth={262} marginBottom={16}>
                        Bills
                    </H6Regular>
                    {billsWidget}
                </YStack>
            </Animated.View>
        </View>
    );
};

export default BillsOnHome;
